import { BOSS, CREATURE_BY_ID, ENCANTO, GLYPHS, STATUS_BY_ID, STATUS_IDS } from "@eldrun/content";
import { h } from "../kit/dom.ts";

export interface BossLike {
  type: string;
  source: string;
  elite: boolean;
  phase: number;
}

export interface CreatureHp {
  hp: number;
  hpMax: number;
  /** Status (F76): a barra destaca o Exposto. */
  status?: number;
}

const EXPOSED_BIT = 1 << STATUS_IDS.indexOf("exposto");

export interface ChallengeLike {
  left: number;
  outside: number;
}

export interface BossBar {
  id: string;
  name: string;
  /** Vida de 0 a 1. */
  fraction: number;
  /** "reforçado · fase 2" e afins. */
  tag: string;
  /** Exposto (F76, doc 09 §5.2): a janela de dano, com o glifo ◎ e o brilho na barra. */
  exposed: boolean;
}

/** Até duas barras (doc 01): a da noite primeiro, depois a da Pedra de Encanto. */
export const MAX_BOSS_BARS = 2;

export function bossBars(bosses: Iterable<[string, BossLike]>, creatureOf: (id: string) => CreatureHp | undefined): BossBar[] {
  const out: (BossBar & { order: number })[] = [];
  for (const [id, b] of bosses) {
    const c = creatureOf(id);
    if (!c || c.hpMax <= 0) continue;
    const tags = [b.elite ? "reforçado" : "", b.phase >= 2 ? "fase 2" : ""].filter(Boolean);
    out.push({ id, name: CREATURE_BY_ID.get(b.type)?.name ?? b.type, fraction: Math.max(0, Math.min(1, c.hp / c.hpMax)), tag: tags.join(" · "), exposed: ((c.status ?? 0) & EXPOSED_BIT) !== 0, order: b.source === "noite" ? 0 : 1 });
  }
  return out
    .sort((a, b) => a.order - b.order)
    .slice(0, MAX_BOSS_BARS)
    .map(({ order: _order, ...bar }) => bar);
}

/** Nome do chefe com o artigo ("o Ciclope"), como as frases pedem; sem a criatura, `fallback`. */
export function bossName(type: string, fallback: string): string {
  const c = CREATURE_BY_ID.get(type);
  if (!c) return fallback;
  return c.article ? `${c.article} ${c.name}` : c.name;
}

/** A frase começando com maiúscula ("O Ciclope vem aí!"). */
export const capitalize = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/** Linha do Desafio em curso: quantas faltam e, fora da arena, quanto tempo resta para voltar. */
export function challengeText(c: ChallengeLike): string {
  const left = `Desafio do Menir · ${c.left} ${c.left === 1 ? "monstro" : "monstros"}`;
  return c.outside > 0 ? `${left} · volte para o círculo (${Math.max(0, ENCANTO.challenge.outside - c.outside)} s)` : left;
}

/** Barras de chefe no alto da tela (nome, vida e etiqueta) e a linha do Desafio. */
export class BossBarsHud {
  readonly element: HTMLElement;
  private readonly rows: { root: HTMLElement; name: HTMLElement; fill: HTMLElement; exposed: HTMLElement; cast: HTMLElement; castName: HTMLElement; castFill: HTMLElement; id: string }[] = [];
  private readonly challenge: HTMLElement;
  private key = "";

  constructor(parent: HTMLElement) {
    this.challenge = h("p", { class: "desafio" });
    this.element = h("div", { id: "chefes", "aria-live": "polite" });
    for (let i = 0; i < MAX_BOSS_BARS; i++) {
      const name = h("span", { class: "nome" });
      const fill = h("span", { class: "vida" });
      // F63: a coroa (o glifo do chefe), as marcas das quebras de postura e a barra de conjuração.
      const marks = BOSS.breaks.map((f) => {
        const mark = h("span", { class: "fase", "aria-hidden": "true" });
        mark.style.left = `${(f * 100).toFixed(1)}%`;
        return mark;
      });
      const castName = h("span", { class: "golpe" });
      const castFill = h("span", { class: "progresso" });
      const cast = h("div", { class: "conjuracao" }, castName, h("span", { class: "trilho" }, castFill));
      cast.hidden = true;
      const exposed = h("span", { class: "exposto", title: STATUS_BY_ID.get("exposto")!.name }, `${STATUS_BY_ID.get("exposto")!.glyph} ${STATUS_BY_ID.get("exposto")!.name}`);
      exposed.hidden = true;
      const root = h("div", { class: "chefe" }, h("span", { class: "titulo" }, h("span", { class: "coroa", "aria-hidden": "true" }, GLYPHS.coroa.char), name, exposed), h("span", { class: "barra" }, fill, ...marks), cast);
      root.hidden = true;
      this.rows.push({ root, name, fill, exposed, cast, castName, castFill, id: "" });
      this.element.appendChild(root);
    }
    this.element.appendChild(this.challenge);
    this.challenge.hidden = true;
    parent.appendChild(this.element);
  }

  update(bars: readonly BossBar[], challenge: string | undefined): void {
    const key = `${bars.map((b) => `${b.id}:${b.name}:${b.tag}:${b.fraction.toFixed(3)}:${b.exposed}`).join("|")}#${challenge ?? ""}`;
    if (key === this.key) return;
    this.key = key;
    this.rows.forEach((row, i) => {
      const bar = bars[i];
      row.root.hidden = !bar;
      if (!bar) return;
      row.id = bar.id;
      row.name.textContent = bar.tag ? `${bar.name} · ${bar.tag}` : bar.name;
      row.fill.style.width = `${(bar.fraction * 100).toFixed(1)}%`;
      row.exposed.hidden = !bar.exposed;
      row.root.classList.toggle("exposto", bar.exposed);
    });
    this.challenge.hidden = challenge === undefined;
    this.challenge.textContent = challenge ?? "";
  }

  /**
   * Conjuração (F63): o golpe do chefe `id` com o nome e a barra que enche até o impacto (`ms`); some
   * logo depois. Pela animação CSS (`--dur`), sem tocar no DOM a cada quadro.
   */
  cast(id: string, label: string, ms: number): void {
    const row = this.rows.find((r) => r.id === id && !r.root.hidden);
    if (!row) return;
    row.castName.textContent = label;
    row.cast.hidden = false;
    row.cast.style.setProperty("--dur", `${Math.max(100, Math.round(ms))}ms`);
    row.castFill.classList.remove("enche");
    void row.castFill.offsetWidth;
    row.castFill.classList.add("enche");
    clearTimeout(this.castTimers.get(id));
    this.castTimers.set(id, setTimeout(() => (row.cast.hidden = true), ms + 400));
  }

  /** Corta a conjuração do chefe `id` (F69: interrompido, ou cambaleou). */
  stopCast(id: string): void {
    const row = this.rows.find((r) => r.id === id);
    if (!row || row.cast.hidden) return;
    clearTimeout(this.castTimers.get(id));
    row.cast.hidden = true;
  }

  private readonly castTimers = new Map<string, ReturnType<typeof setTimeout>>();

  /** Texto visível (testes e2e). */
  get text(): string {
    return [...this.rows.filter((r) => !r.root.hidden).map((r) => r.name.textContent ?? ""), this.challenge.hidden ? "" : (this.challenge.textContent ?? "")].filter(Boolean).join(" | ");
  }
}
